// Facilities API service
import { apiService } from './api';
import type { FacilitySettings } from '../components/Forms/FacilitySettingsModal';
import {
  Facility, Location, Client, Task, AssetTypeDef, LabIncidentListItem, CheckpointSummary,
  User,
  FacilityCreate,
  FacilityUpdate,
  FacilityStaffAssignment,
  FacilityStaffAssignmentCreate,
  FacilityStaffAssignmentUpdate,
  Asset,
  Inspection,
  Audit,
  FacilityFilters,
  PaginationParams
} from '../types';

type FacilitySettingsPayload = {
  allow_sub_assets: boolean;
  require_location_for_assets: boolean;
  auto_generate_asset_ids: boolean;
  require_photos_on_inspection: boolean;
  allow_offline_inspections: boolean;
  min_checkpoint_completion_rate: number;
  notify_on_asset_added: boolean;
  notify_on_inspection_complete: boolean;
  notify_on_high_risk_findings: boolean;
  restrict_asset_editing: boolean;
  require_approval_for_asset_deletion: boolean;
};

const fromSettingsPayload = (settings: FacilitySettingsPayload): FacilitySettings => ({
  allowSubAssets: settings.allow_sub_assets,
  requireLocationForAssets: settings.require_location_for_assets,
  autoGenerateAssetIds: settings.auto_generate_asset_ids,
  requirePhotosOnInspection: settings.require_photos_on_inspection,
  allowOfflineInspections: settings.allow_offline_inspections,
  minCheckpointCompletionRate: settings.min_checkpoint_completion_rate,
  notifyOnAssetAdded: settings.notify_on_asset_added,
  notifyOnInspectionComplete: settings.notify_on_inspection_complete,
  notifyOnHighRiskFindings: settings.notify_on_high_risk_findings,
  restrictAssetEditing: settings.restrict_asset_editing,
  requireApprovalForAssetDeletion: settings.require_approval_for_asset_deletion,
});

const toSettingsPayload = (settings: FacilitySettings): FacilitySettingsPayload => ({
  allow_sub_assets: settings.allowSubAssets,
  require_location_for_assets: settings.requireLocationForAssets,
  auto_generate_asset_ids: settings.autoGenerateAssetIds,
  require_photos_on_inspection: settings.requirePhotosOnInspection,
  allow_offline_inspections: settings.allowOfflineInspections,
  min_checkpoint_completion_rate: settings.minCheckpointCompletionRate,
  notify_on_asset_added: settings.notifyOnAssetAdded,
  notify_on_inspection_complete: settings.notifyOnInspectionComplete,
  notify_on_high_risk_findings: settings.notifyOnHighRiskFindings,
  restrict_asset_editing: settings.restrictAssetEditing,
  require_approval_for_asset_deletion: settings.requireApprovalForAssetDeletion,
});

export interface FacilityPage {
  facility: Facility;
  assets: Asset[];
  locations: Location[];
  client: Client | null;
  tasks: Task[];
  check_summary: {not_done:number;overdue:number};
  checks_due: number;
  open_incidents: LabIncidentListItem[];
  inspections: Inspection[];
  asset_types: AssetTypeDef[];
  assignable_users: User[];
  checkpoints?: CheckpointSummary[];
}

export class FacilitiesService {
  async getPage(id: string, checkpoints = false): Promise<FacilityPage> {
    return apiService.get<FacilityPage>(`/facilities/${id}/page`, checkpoints ? {include: 'checkpoints'} : undefined);
  }

  async getAssignableUsers(id: string): Promise<User[]> {
    return apiService.get<User[]>(`/facilities/${id}/assignable-users`);
  }

  async getFacilities(params?: FacilityFilters): Promise<Facility[]> {
    return apiService.get<Facility[]>('/facilities/', params);
  }

  async getFacilityById(id: string): Promise<Facility> {
    return apiService.get<Facility>(`/facilities/${id}`);
  }

  async createFacility(facilityData: FacilityCreate): Promise<Facility> {
    return apiService.post<Facility>('/facilities/', facilityData);
  }

  async updateFacility(id: string, facilityData: FacilityUpdate): Promise<Facility> {
    return apiService.put<Facility>(`/facilities/${id}`, facilityData);
  }

  async getDeletionSummary(id: string): Promise<{assets: number; inspections: number; actions: number}> {
    return apiService.get(`/facilities/${id}/deletion-summary`);
  }

  async deleteFacility(id: string): Promise<void> {
    return apiService.delete<void>(`/facilities/${id}`);
  }

  async getSettings(facilityId: string): Promise<FacilitySettings> {
    const settings = await apiService.get<FacilitySettingsPayload>(
      `/facilities/${facilityId}/settings`
    );
    return fromSettingsPayload(settings);
  }

  async saveSettings(
    facilityId: string,
    settings: FacilitySettings
  ): Promise<FacilitySettings> {
    const savedSettings = await apiService.put<FacilitySettingsPayload>(
      `/facilities/${facilityId}/settings`,
      toSettingsPayload(settings)
    );
    return fromSettingsPayload(savedSettings);
  }

  async getFacilityAssets(
    facilityId: string,
    params?: PaginationParams
  ): Promise<Asset[]> {
    if (params) return apiService.get<Asset[]>(`/facilities/${facilityId}/assets`, params);
    const assets: Asset[] = [];
    const limit = 1000;
    for (let skip = 0; ; skip += limit) {
      const page = await apiService.get<Asset[]>(`/facilities/${facilityId}/assets`, {skip, limit});
      assets.push(...page);
      if (page.length < limit) return assets;
    }
  }

  async getFacilityInspections(
    facilityId: string,
    params?: PaginationParams
  ): Promise<Inspection[]> {
    return apiService.get<Inspection[]>(`/facilities/${facilityId}/inspections`, params);
  }

  async getFacilityAudits(
    facilityId: string,
    params?: PaginationParams
  ): Promise<Audit[]> {
    return apiService.get<Audit[]>(`/facilities/${facilityId}/audits`, params);
  }

  async getFacilityStaff(facilityId: string): Promise<FacilityStaffAssignment[]> {
    return apiService.get<FacilityStaffAssignment[]>(`/facilities/${facilityId}/staff`);
  }

  async addFacilityStaff(
    facilityId: string,
    data: FacilityStaffAssignmentCreate
  ): Promise<FacilityStaffAssignment> {
    return apiService.post<FacilityStaffAssignment>(`/facilities/${facilityId}/staff`, data);
  }

  async updateFacilityStaff(
    facilityId: string,
    userId: string,
    data: FacilityStaffAssignmentUpdate
  ): Promise<FacilityStaffAssignment> {
    return apiService.put<FacilityStaffAssignment>(
      `/facilities/${facilityId}/staff/${userId}`,
      data
    );
  }

  async removeFacilityStaff(facilityId: string, userId: string): Promise<void> {
    return apiService.delete<void>(`/facilities/${facilityId}/staff/${userId}`);
  }
}

export const facilitiesService = new FacilitiesService();
